import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import PrimaryNavigation from 'components/ui/PrimaryNavigation';
import UserContextMenu from 'components/ui/UserContextMenu';
import QuickActionPanel from 'components/ui/QuickActionPanel';
import SummaryStats from './components/SummaryStats';
import SpendingTrendsChart from './components/SpendingTrendsChart';
import CategoryDistribution from './components/CategoryDistribution';
import BudgetOverview from './components/BudgetOverview';
import FinancialGoals from './components/FinancialGoals';
import SpendingAlerts from './components/SpendingAlerts';
import RecentTransactions from './components/RecentTransactions';
import ARScannerCTA from './components/ARScannerCTA';
import MLInsights from './components/MLInsights';
import GuidedTour from './components/GuidedTour';
import Icon from 'components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import {
  fetchSummaryStats,
  fetchSpendingCategories,
  fetchRecentTransactions,
  fetchFinancialGoals,
  fetchSpendingTrends,
  fetchCurrentBudget,
} from '../../services/dashboardService';

function NavRightSlotPortal({ user }) {
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => { setMounted(true); }, []);
  if (!mounted) return null;
  const slot = document.getElementById('nav-right-slot');
  if (!slot) return null;
  return createPortal(<UserContextMenu user={user} />, slot);
}

export default function HomeDashboard() {
  const { user, userProfile } = useAuth();
  const [greeting] = useState(() => {
    const h = new Date()?.getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  });

  const [summaryStats, setSummaryStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [goals, setGoals] = useState([]);
  const [trends, setTrends] = useState([]);
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Tour state
  const [tourActive, setTourActive] = useState(false);
  const [showTourHint, setShowTourHint] = useState(false);

  // Auto-launch tour for first-time users after data loads
  useEffect(() => {
    if (!loading) {
      const completed = localStorage.getItem('finvisar_tour_completed');
      if (!completed) {
        const timer = setTimeout(() => setTourActive(true), 800);
        return () => clearTimeout(timer);
      }
    }
  }, [loading]);

  useEffect(() => {
    if (!user?.id) return;
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [stats, cats, txs, gls, trds, bdg] = await Promise.all([
          fetchSummaryStats(user?.id),
          fetchSpendingCategories(user?.id),
          fetchRecentTransactions(user?.id, 10),
          fetchFinancialGoals(user?.id),
          fetchSpendingTrends(user?.id),
          fetchCurrentBudget(user?.id),
        ]);
        setSummaryStats(stats);
        setCategories(cats);
        setTransactions(txs);
        setGoals(gls);
        setTrends(trds);
        setBudget(bdg);
      } catch (err) {
        setError(err?.message || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [user?.id]);

  const displayUser = {
    name: userProfile?.full_name || user?.email?.split('@')?.[0] || 'User',
    email: user?.email || '',
    role: userProfile?.role || 'consumer',
    avatar: userProfile?.avatar_url || '',
  };

  const firstName = displayUser?.name?.split(' ')?.[0] || 'there';
  const currentMonthLabel = new Date()?.toLocaleString('default', { month: 'long', year: 'numeric' });
  const budgetData = { remaining: summaryStats?.remaining || 0 };

  const handleRelaunchTour = () => {
    setTourActive(false);
    setTimeout(() => setTourActive(true), 100);
  };

  return (
    <div className="min-h-screen bg-[#020617]">
      <PrimaryNavigation />
      <NavRightSlotPortal user={displayUser} />
      <main className="pb-20 md:pb-8 px-4 md:px-6 lg:px-8 max-w-[1400px] mx-auto">
        <div className="py-5 md:py-6 lg:py-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="font-heading font-bold text-2xl md:text-3xl text-[#F8FAFC]">
                {greeting}, {firstName} 👋
              </h1>
              <p className="text-sm md:text-base text-[#94A3B8] mt-1">
                Here's your financial overview for <span className="text-[#06B6D4] font-medium">{currentMonthLabel}</span>
              </p>
            </div>
            <div className="flex items-center gap-2">
              {loading ? (
                <div className="flex items-center gap-1.5 bg-[rgba(6,182,212,0.1)] border border-[rgba(6,182,212,0.2)] px-3 py-1.5 rounded-lg">
                  <div className="w-2 h-2 rounded-full bg-[#06B6D4] animate-pulse" />
                  <span className="text-xs text-[#06B6D4] font-medium">Loading...</span>
                </div>
              ) : (
                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border ${
                  (summaryStats?.remaining || 0) >= 0
                    ? 'bg-[rgba(16,185,129,0.1)] border-[rgba(16,185,129,0.2)]'
                    : 'bg-[rgba(239,68,68,0.1)] border-[rgba(239,68,68,0.2)]'
                }`}>
                  <div className={`w-2 h-2 rounded-full animate-pulse ${
                    (summaryStats?.remaining || 0) >= 0 ? 'bg-[#10B981]' : 'bg-[#EF4444]'
                  }`} />
                  <span className={`text-xs font-medium ${
                    (summaryStats?.remaining || 0) >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'
                  }`}>
                    {(summaryStats?.remaining || 0) >= 0 ? 'Budget on track' : 'Over budget'}
                  </span>
                </div>
              )}

              {/* Tour relaunch button */}
              <div className="relative group">
                <button
                  onClick={handleRelaunchTour}
                  aria-label="Take a Tour"
                  className="w-9 h-9 flex items-center justify-center rounded-full border border-[rgba(0,229,255,0.4)] bg-[rgba(0,229,255,0.06)] hover:bg-[rgba(0,229,255,0.12)] hover:border-[rgba(0,229,255,0.7)] transition-all duration-200 text-[#00E5FF] font-bold text-sm"
                >
                  ?
                </button>
                <div className="absolute right-0 top-full mt-1.5 px-2.5 py-1 bg-[#0F172A] border border-[rgba(0,229,255,0.2)] rounded-lg text-xs text-[#94A3B8] whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                  Take a Tour
                </div>
              </div>

              <button className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#0F172A] border border-[rgba(6,182,212,0.1)] hover:border-[rgba(6,182,212,0.25)] transition-all" aria-label="Notifications">
                <Icon name="Bell" size={18} color="#94A3B8" />
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-5 flex items-center gap-2 p-4 rounded-xl bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.2)]">
            <Icon name="AlertCircle" size={16} color="#EF4444" />
            <p className="text-sm text-[#EF4444]">{error}</p>
          </div>
        )}

        <section className="mb-5 md:mb-6" aria-label="Financial summary statistics">
          <SummaryStats stats={summaryStats} loading={loading} />
        </section>

        <section className="mb-5 md:mb-6" aria-label="Spending alerts and notifications">
          <SpendingAlerts categories={categories} loading={loading} />
        </section>

        <section className="mb-5 md:mb-6" aria-label="AR scanner quick access">
          <ARScannerCTA />
        </section>

        <section className="mb-5 md:mb-6" aria-label="Quick actions panel">
          <QuickActionPanel budgetData={budgetData} />
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5 mb-5 md:mb-6" aria-label="Spending charts and analytics">
          <div className="lg:col-span-2">
            <SpendingTrendsChart trends={trends} loading={loading} />
          </div>
          <div>
            <CategoryDistribution categories={categories} loading={loading} />
          </div>
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5 mb-5 md:mb-6" aria-label="Budget overview and financial goals">
          <BudgetOverview categories={categories} loading={loading} />
          <FinancialGoals goals={goals} loading={loading} />
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5" aria-label="AI insights and recent transactions">
          <MLInsights />
          <RecentTransactions transactions={transactions} loading={loading} />
        </section>
      </main>

      {/* Guided Tour Overlay */}
      <GuidedTour isActive={tourActive} onClose={() => setTourActive(false)} />
    </div>
  );
}